"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Phone, Mail, Clock, Calendar, Briefcase, User, MessageSquare, CheckCircle2, DollarSign, Upload } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const inputClasses = "rounded-none border-[#CECECE] h-12 focus-visible:ring-[#6B28A1] focus-visible:ring-1 focus-visible:border-[#6B28A1]";
const textareaClasses = "rounded-none border-[#CECECE] min-h-[150px] focus-visible:ring-[#6B28A1] focus-visible:ring-1 focus-visible:border-[#6B28A1]";
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="108320" className="flex flex-col items-center justify-center p-12 text-center bg-accent/30 border border-[#CECECE] rounded-none space-y-6">
                <div className="size-16 rounded-full bg-[#6B28A1]/10 flex items-center justify-center text-[#6B28A1] mb-2">
                    <CheckCircle2 className="size-8" />
                </div>
                <h3 className="text-2xl font-bold text-[#362242] uppercase tracking-widest">Intake Received</h3>
                <p className="text-muted-foreground max-w-md leading-relaxed">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-4 rounded-full border-[#6B28A1] text-[#6B28A1] hover:bg-[#6B28A1] hover:text-white transition-all uppercase tracking-widest text-xs font-bold px-8 h-12">
                    Submit Another Request
                </Button>
            </div>;
  }
  const serviceOptions = ["Subsurface Soil Investigation (Borings / Test Pits)", "Geotechnical Engineering & Foundation Design", "Bearing Capacity Evaluation", "Stormwater Infiltration Testing", "Perc Testing / Septic Evaluation", "Laboratory Soil Testing", "Residential Site Evaluation", "General Inquiry / Other"];
  return <Form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField control={control} name="name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 font-bold text-[#362242] uppercase tracking-wider text-xs">
                                <User className="size-4 text-[#6B28A1]" />
                                Full Name
                            </FormLabel>
                            <FormControl>
                                <Input type="text" className={inputClasses} placeholder="Jane Doe" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px] uppercase tracking-tight">Enter your first and last name.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 font-bold text-[#362242] uppercase tracking-wider text-xs">
                                <Mail className="size-4 text-[#6B28A1]" />
                                Email Address
                            </FormLabel>
                            <FormControl>
                                <Input type="email" className={inputClasses} placeholder="jane@example.com" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px] uppercase tracking-tight">We'll use this to contact you.</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 font-bold text-[#362242] uppercase tracking-wider text-xs">
                                <Phone className="size-4 text-[#6B28A1]" />
                                Phone Number
                            </FormLabel>
                            <FormControl>
                                <Input type="tel" className={inputClasses} placeholder="(555) 123‑1234" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px] uppercase tracking-tight">Include country code if outside the US.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="service_interest" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 font-bold text-[#362242] uppercase tracking-wider text-xs">
                                <Briefcase className="size-4 text-[#6B28A1]" />
                                Service Interest
                            </FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                    <SelectTrigger className={inputClasses}>
                                        <SelectValue placeholder="Choose a service" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent className="rounded-none">
                                    {serviceOptions.map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                                            {option}
                                        </SelectItem>)}
                                </SelectContent>
                            </Select>
                            <FormDescription className="text-[10px] uppercase tracking-tight">Select the type of service you are interested in.</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 font-bold text-[#362242] uppercase tracking-wider text-xs">
                                <SafeHydrate><Calendar className="size-4 text-[#6B28A1]" /></SafeHydrate>
                                Preferred Appointment Date
                            </FormLabel>
                            <FormControl>
                                <Input type="date" className={inputClasses} {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px] uppercase tracking-tight">Select a date that works best for you.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="budget" rules={{
        min: {
          value: 0,
          message: "Budget must be a positive number"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 font-bold text-[#362242] uppercase tracking-wider text-xs">
                                <DollarSign className="size-4 text-[#6B28A1]" />
                                Estimated Budget (USD)
                            </FormLabel>
                            <FormControl>
                                <Input type="number" className={inputClasses} placeholder="e.g., 1500" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px] uppercase tracking-tight">Optional – helps us tailor a proposal.</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" rules={{
      maxLength: {
        value: 1000,
        message: "Details cannot exceed 1000 characters"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="flex items-center gap-2 font-bold text-[#362242] uppercase tracking-wider text-xs">
                            <MessageSquare className="size-4 text-[#6B28A1]" />
                            Additional Details
                        </FormLabel>
                        <FormControl>
                            <Textarea className={textareaClasses} placeholder="Tell us more about your project, goals, or any special requirements." {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="attachment" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem>
                        <FormLabel className="flex items-center gap-2 font-bold text-[#362242] uppercase tracking-wider text-xs">
                            <Upload className="size-4 text-[#6B28A1]" />
                            Upload Plans / Surveys
                        </FormLabel>
                        <FormControl>
                            <Input type="file" className="rounded-none border-[#CECECE] h-12 file:mr-4 file:py-2 file:px-4 file:rounded-none file:border-0 file:text-xs file:font-bold file:uppercase file:bg-[#6B28A1]/10 file:text-[#6B28A1] hover:file:bg-[#6B28A1]/20 cursor-pointer pt-2" onChange={e => onChange(e.target.files?.[0])} {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px] uppercase tracking-tight">Upload any relevant site plans, surveys, or drawings (max 10MB).</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none">
                    <p className="text-destructive text-sm font-medium">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[280px] rounded-full bg-[#6B28A1] hover:bg-[#6B28A1]/90 text-white font-bold uppercase tracking-widest h-14 text-sm shadow-lg shadow-[#6B28A1]/20 transition-all hover:-translate-y-0.5">
                {isSubmitting ? <>
                        <Loader2 className="animate-spin mr-2 size-5" />
                        Processing...
                    </> : 'Submit Intake Form'}
            </Button>
        </Form>;
}
export default function ConsultationRequestForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section id="intake-form" className="bg-white py-24 md:py-32 relative overflow-hidden text-foreground">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0 opacity-20">
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#CECECE] to-transparent" />
                <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#CECECE] to-transparent" />
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-[#6B28A1]/5 blur-3xl" />
                <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-[#6B28A1]/5 blur-3xl" />
            </div>

            <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                <div className="mb-20 max-w-3xl">
                    <div className="inline-block px-3 py-1 bg-[#6B28A1]/10 text-[#6B28A1] text-[10px] font-bold uppercase tracking-[0.3em] mb-6">
                        Technical Discovery
                    </div>
                    <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-[#362242] mb-8 uppercase">
                        Project <span className="text-[#6B28A1]">Intake Form</span>
                    </h2>
                    <p className="text-xl text-muted-foreground leading-relaxed font-light">
                        Precision starts with clarity. Provide the technical parameters of your project below to help our team engineer the perfect solution for your vision.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
                    <div className="lg:col-span-2 bg-white text-card-foreground rounded-none border border-[#CECECE] p-8 md:p-12 shadow-[20px_20px_60px_-15px_rgba(0,0,0,0.05)]">
                        <FormProvider formKey="intake_form_intakeform" formId="intake_form_intakeform" sectionName="Intake Form" defaultValues={{
            name: "",
            email: "",
            phone: "",
            preferred_date: "",
            service_interest: "",
            budget: "",
            message: "",
            attachment: ""
          }} submitText="Submit Intake Form" successMessage="Thank you for your submission. Our technical lead will review your requirements and reach out within 24 hours to schedule a deep-dive session.">
                            <ConsultationFormInner />
                        </FormProvider>
                    </div>

                    <div className="lg:col-span-1">
                        <Card className="sticky top-24 border-none shadow-none rounded-none bg-[#362242] text-white overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 -mr-16 -mt-16 rotate-45 pointer-events-none" />
                            <CardHeader className="pb-8 pt-10">
                                <CardTitle className="text-2xl font-bold text-white uppercase tracking-widest">Why This Matters</CardTitle>
                                <CardDescription className="text-base text-white/60 leading-relaxed mt-2">Early project coordination ensures the right geotechnical investigation and soil testing program is planned from the start.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-10 pb-12">
                                <div className="flex items-start gap-5">
                                    <div className="mt-1 bg-white/10 p-3 rounded-none text-[#6B28A1] shrink-0 border border-white/5">
                                        <Briefcase className="size-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white mb-2 uppercase tracking-wider text-sm">Streamlined Discovery</h4>
                                        <p className="text-sm text-white/50 leading-relaxed">By understanding your project scope early, we can move efficiently into planning the required soil testing, subsurface investigation, and geotechnical evaluation needed to support your project.</p>
                                    </div>
                                </div>
                                
                                <Separator className="bg-white/10" />

                                <div className="flex items-start gap-5">
                                    <div className="mt-1 bg-white/10 p-3 rounded-none text-[#6B28A1] shrink-0 border border-white/5">
                                        <Clock className="size-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white mb-2 uppercase tracking-wider text-sm">Priority Review</h4>
                                        <p className="text-sm text-white/50 leading-relaxed">Submitted project details are reviewed promptly to determine site investigation needs, testing requirements, and next steps for geotechnical evaluation.</p>
                                        <div className="mt-4 flex items-center gap-2">
                                            <span className="size-1.5 rounded-full bg-[#6B28A1] animate-pulse" />
                                            <p className="text-[10px] text-[#6B28A1] font-bold uppercase tracking-widest">Avg Reply: 4 Hours</p>
                                        </div>
                                    </div>
                                </div>

                                <Separator className="bg-white/10" />

                                <div className="flex items-start gap-5">
                                    <div className="mt-1 bg-white/10 p-3 rounded-none text-[#6B28A1] shrink-0 border border-white/5">
                                        <Mail className="size-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white mb-2 uppercase tracking-wider text-sm">Direct Support</h4>
                                        <p className="text-sm text-white/50 font-medium">info@qstgeo.com</p>
                                        <p className="text-[10px] text-white/30 mt-2 uppercase tracking-tight">
                                            Need help? Reach out directly.
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </section>;
}